import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getTeacherStudents, getStudentCourses, getStudentNote, addStudentNote, getStudentStats } from '../../services/teacherService';
import { User, StudentProgress, StudentEnrollment } from '../../types';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Alert, 
  Spinner, 
  Form, 
  InputGroup, 
  Button, 
  Modal, 
  Badge, 
  Tab, 
  Tabs,
  ListGroup,
  ProgressBar
} from 'react-bootstrap';
import { FaSearch, FaUsers, FaEye, FaEdit, FaGraduationCap, FaChartLine, FaBookOpen, FaVideo, FaClock, FaChartBar, FaCheckCircle, FaCalendarAlt } from 'react-icons/fa';

const StudentsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [students, setStudents] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState<User | null>(null);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [studentProgress, setStudentProgress] = useState<StudentProgress | null>(null);
  const [studentCourses, setStudentCourses] = useState<Array<StudentEnrollment & { completedLessonsCount?: number; totalLessons?: number }>>([]);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [studentNote, setStudentNote] = useState('');
  const [noteUpdatedAt, setNoteUpdatedAt] = useState<number | undefined>(undefined);
  const [loadingNote, setLoadingNote] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [studentStats, setStudentStats] = useState<{
    totalCourses: number;
    totalLessonsWatched: number;
    totalWatchTime: number;
    averageProgress: number;
    lastActivity?: number;
    completionRate: number;
  } | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      if (!currentUser) {
        setError('يجب تسجيل الدخول لعرض الطلاب');
        return;
      }

      setLoading(true);
      setError(null);
      const fetchedStudents = await getTeacherStudents(currentUser.uid);
      setStudents(fetchedStudents);
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('حدث خطأ أثناء جلب بيانات الطلاب');
    } finally {
      setLoading(false);
    }
  };

  const handleViewStudent = async (student: User) => {
    setSelectedStudent(student);
    setShowStudentModal(true);
    
    try {
      setLoadingProgress(true);
      setLoadingNote(true);
      setLoadingStats(true);
      
      // جلب الدورات المسجل فيها الطالب
      if (currentUser) {
        console.log(`جاري جلب دورات الطالب: ${student.id}`);
        const courses = await getStudentCourses(student.id, currentUser.uid);
        console.log('دورات الطالب:', courses);
        setStudentCourses(courses);
        
        // جلب ملاحظات الطالب
        const noteData = await getStudentNote(student.id, currentUser.uid);
        if (noteData) {
          setStudentNote(noteData.note);
          setNoteUpdatedAt(noteData.updatedAt);
        } else {
          setStudentNote('');
          setNoteUpdatedAt(undefined);
        }
        
        // جلب إحصائيات الطالب
        const stats = await getStudentStats(student.id, currentUser.uid);
        console.log('إحصائيات الطالب:', stats);
        setStudentStats(stats);
      }
    } catch (error) {
      console.error('Error fetching student data:', error);
    } finally {
      setLoadingProgress(false);
      setLoadingNote(false);
      setLoadingStats(false);
    }
  };

  const handleSaveNote = async () => {
    if (!selectedStudent || !currentUser) return;
    
    try {
      setSavingNote(true);
      await addStudentNote(selectedStudent.id, currentUser.uid, studentNote);
      setNoteUpdatedAt(Date.now());
    } catch (error) {
      console.error('Error saving note:', error);
    } finally {
      setSavingNote(false);
    }
  };

  const handleCloseModal = () => {
    setShowStudentModal(false);
    setSelectedStudent(null);
    setStudentProgress(null);
    setStudentCourses([]);
    setStudentNote('');
    setNoteUpdatedAt(undefined);
    setStudentStats(null);
  };

  const formatDate = (timestamp: number | undefined) => {
    if (!timestamp) return 'غير متوفر';
    return new Date(timestamp).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // تنسيق وقت المشاهدة
  const formatWatchTime = (seconds: number) => {
    if (seconds < 60) {
      return `${seconds} ثانية`;
    } else if (seconds < 3600) {
      return `${Math.floor(seconds / 60)} دقيقة`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours} ساعة ${minutes > 0 ? `و ${minutes} دقيقة` : ''}`;
    }
  };

  const filteredStudents = students.filter(student => 
    student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container fluid className="py-4">
      {loading && (
        <div className="text-center my-4">
          <Spinner animation="border" role="status" />
          <p className="mt-2">جاري تحميل البيانات...</p>
        </div>
      )}

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}
      
      {/* Page Header */}
      <Row className="mb-4">
        <Col>
          <h1 className="mb-4">
            <FaUsers className="me-2" />
            إدارة الطلاب
          </h1>
          <p className="text-muted">يمكنك من هنا عرض جميع الطلاب المسجلين في دوراتك ومتابعة تقدمهم</p>
        </Col>
      </Row>
      
      {/* Students List */}
      <Card>
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h2>
                <FaGraduationCap className="me-2" />
                الطلاب المسجلين
              </h2>
            </Col>
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control 
                  type="text" 
                  placeholder="ابحث عن الطلاب..." 
                  value={searchTerm} 
                  onChange={(e) => setSearchTerm(e.target.value)} 
                />
              </InputGroup>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {!loading && (
            <>
              {filteredStudents.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>اسم الطالب</th>
                        <th>البريد الإلكتروني</th>
                        <th>رقم الهاتف</th>
                        <th>تاريخ التسجيل</th>
                        <th>الحالة</th>
                        <th>الإجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStudents.map((student, index) => (
                        <tr key={student.id}>
                          <td>{index + 1}</td>
                          <td>{student.name}</td>
                          <td>{student.email}</td>
                          <td>{student.phoneNumber || 'غير متوفر'}</td>
                          <td>{student.createdAt ? formatDate(student.createdAt) : 'غير متوفر'}</td>
                          <td>
                            {student.isVerified ? (
                              <Badge bg="success">مفعل</Badge>
                            ) : (
                              <Badge bg="warning">غير مفعل</Badge>
                            )}
                          </td>
                          <td>
                            <Button 
                              variant="outline-primary" 
                              size="sm" 
                              onClick={() => handleViewStudent(student)}
                              className="me-2"
                            >
                              <FaEye /> عرض التفاصيل
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Alert variant="info">
                  <div className="text-center py-5">
                    <FaUsers size={48} className="mb-3 text-muted" />
                    <h4>لا يوجد طلاب مسجلين</h4>
                    <p className="text-muted">لم يتم العثور على طلاب مسجلين في دوراتك حالياً</p>
                  </div>
                </Alert>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Student Details Modal */}
      <Modal 
        show={showStudentModal} 
        onHide={handleCloseModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaGraduationCap className="me-2" />
            تفاصيل الطالب
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedStudent && (
            <Tabs defaultActiveKey="info" id="student-tabs" className="mb-3">
              <Tab eventKey="info" title="معلومات الطالب">
                <Row>
                  <Col md={6}>
                    <ListGroup variant="flush">
                      <ListGroup.Item>
                        <strong>الاسم:</strong> {selectedStudent?.name}
                      </ListGroup.Item>
                      <ListGroup.Item>
                        <strong>البريد الإلكتروني:</strong> {selectedStudent?.email}
                      </ListGroup.Item>
                      <ListGroup.Item>
                        <strong>رقم الهاتف:</strong> {selectedStudent?.phoneNumber || 'غير متوفر'}
                      </ListGroup.Item>
                    </ListGroup>
                  </Col>
                  <Col md={6}>
                    <ListGroup variant="flush">
                      <ListGroup.Item>
                        <strong>تاريخ التسجيل:</strong> {selectedStudent?.createdAt ? formatDate(selectedStudent.createdAt) : 'غير متوفر'}
                      </ListGroup.Item>
                      <ListGroup.Item>
                        <strong>حالة الحساب:</strong> {' '}
                        {selectedStudent?.isVerified ? (
                          <Badge bg="success">مفعل</Badge>
                        ) : (
                          <Badge bg="warning">غير مفعل</Badge>
                        )}
                      </ListGroup.Item>
                    </ListGroup>
                  </Col>
                </Row>
              </Tab>
              <Tab eventKey="courses" title="الدورات المسجلة">
                {loadingProgress ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" role="status" />
                    <p className="mt-2">جاري تحميل البيانات...</p>
                  </div>
                ) : studentCourses.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>اسم الدورة</th>
                          <th>تاريخ التسجيل</th>
                          <th>تاريخ آخر دخول</th>
                          <th>التقدم</th>
                          <th>الدروس المكتملة</th>
                        </tr>
                      </thead>
                      <tbody>
                        {studentCourses.map((course, index) => (
                          <tr key={course.id}>
                            <td>{index + 1}</td>
                            <td>{course.courseName || 'دورة غير معروفة'}</td>
                            <td>{formatDate(course.enrollmentDate)}</td>
                            <td>{formatDate(course.lastAccessDate)}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                <ProgressBar 
                                  now={course.progress || 0} 
                                  variant={(course.progress || 0) >= 70 ? 'success' : (course.progress || 0) >= 30 ? 'warning' : 'danger'} 
                                  style={{ height: '10px', width: '100px' }} 
                                  className="me-2"
                                />
                                <span>{Math.round(course.progress || 0)}%</span>
                              </div>
                            </td>
                            <td>
                              {course.completedLessonsCount || 0} / {course.totalLessons || 0}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <FaChartLine size={32} className="mb-3 text-primary" />
                    <h5>لا توجد دورات مسجلة</h5>
                    <p className="text-muted">هذا الطالب غير مسجل في أي من دوراتك</p>
                  </div>
                )}
              </Tab>
              <Tab eventKey="notes" title="ملاحظات">
                <Card>
                  <Card.Body>
                    <h5 className="mb-3">ملاحظات المعلم</h5>
                    {loadingNote ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" size="sm" />
                        <p>جاري تحميل الملاحظات...</p>
                      </div>
                    ) : (
                      <>
                        <Form.Group className="mb-3">
                          <Form.Label>أضف ملاحظاتك عن الطالب</Form.Label>
                          <Form.Control 
                            as="textarea" 
                            rows={5} 
                            value={studentNote} 
                            onChange={(e) => setStudentNote(e.target.value)}
                            placeholder="اكتب ملاحظاتك هنا..."
                          />
                        </Form.Group>
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">
                            {noteUpdatedAt ? `آخر تحديث: ${formatDate(noteUpdatedAt)}` : 'لم يتم حفظ أي ملاحظات بعد'}
                          </small>
                          <Button 
                            variant="primary" 
                            onClick={handleSaveNote}
                            disabled={savingNote}
                          >
                            {savingNote ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                جاري الحفظ...
                              </>
                            ) : 'حفظ الملاحظات'}
                          </Button>
                        </div>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Tab>
              <Tab eventKey="stats" title="الإحصائيات">
                <Card>
                  <Card.Body>
                    <h5 className="mb-4">إحصائيات الطالب</h5>
                    {loadingStats ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" size="sm" />
                        <p>جاري تحميل الإحصائيات...</p>
                      </div>
                    ) : studentStats ? (
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="text-center">
                              <FaBookOpen size={32} className="mb-3 text-primary" />
                              <h6>الدورات المسجلة</h6>
                              <h3 className="mb-0">{studentStats.totalCourses || 0}</h3>
                            </Card.Body>
                          </Card>
                        </div>
                        <div className="col-md-6 mb-3">
                          <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="text-center">
                              <FaVideo size={32} className="mb-3 text-success" />
                              <h6>الدروس المشاهدة</h6>
                              <h3 className="mb-0">{studentStats.totalLessonsWatched || 0}</h3>
                            </Card.Body>
                          </Card>
                        </div>
                        <div className="col-md-6 mb-3">
                          <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="text-center">
                              <FaClock size={32} className="mb-3 text-warning" />
                              <h6>وقت المشاهدة</h6>
                              <h3 className="mb-0">{formatWatchTime(studentStats.totalWatchTime || 0)}</h3>
                            </Card.Body>
                          </Card>
                        </div>
                        <div className="col-md-6 mb-3">
                          <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="text-center">
                              <FaChartLine size={32} className="mb-3 text-info" />
                              <h6>متوسط التقدم</h6>
                              <h3 className="mb-0">{(studentStats.averageProgress || 0).toFixed(1)}%</h3>
                            </Card.Body>
                          </Card>
                        </div>
                        <div className="col-md-6 mb-3">
                          <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="text-center">
                              <FaCalendarAlt size={32} className="mb-3 text-secondary" />
                              <h6>آخر نشاط</h6>
                              <h3 className="mb-0">
                                {studentStats.lastActivity ? formatDate(studentStats.lastActivity) : 'لا يوجد نشاط'}
                              </h3>
                            </Card.Body>
                          </Card>
                        </div>
                        <div className="col-md-6 mb-3">
                          <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="text-center">
                              <FaCheckCircle size={32} className="mb-3 text-danger" />
                              <h6>معدل الإكمال</h6>
                              <h3 className="mb-0">{(studentStats.completionRate || 0).toFixed(1)}%</h3>
                            </Card.Body>
                          </Card>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <FaChartBar size={32} className="mb-3 text-primary" />
                        <h5>لا توجد إحصائيات</h5>
                        <p className="text-muted">لم يتم العثور على أي إحصائيات لهذا الطالب</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab>
            </Tabs>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            إغلاق
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default StudentsPage;
